/**
 * WRS Central — 2026 facelift
 * Global MUI component overrides. These cascade across every page so the
 * refreshed look is applied without touching individual page logic.
 */

import colors from "./base/colors.js";
import borders from "./base/borders.js";
import rgba from "./functions/rgba.js";
import linearGradient from "./functions/linearGradient.js";

const { primary, secondary, dark, white, grey, text, light, info, error } =
  colors;
const { borderRadius, borderWidth } = borders;

// Soft, layered elevation used throughout the refreshed UI
const softShadow = `0 1px 2px ${rgba(dark.main, 0.04)}, 0 10px 30px -14px ${rgba(
  dark.main,
  0.22
)}`;
const brandShadow = `0 6px 18px -6px ${rgba(primary.main, 0.45)}`;
const brandShadowHover = `0 10px 24px -6px ${rgba(primary.main, 0.55)}`;

const globalComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        scrollBehavior: "smooth",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
      },
      body: {
        minHeight: "100vh",
        backgroundColor: light.main,
        backgroundImage: `radial-gradient(1200px 600px at 50% -10%, ${rgba(
          secondary.main,
          0.28
        )} 0%, ${rgba(secondary.main, 0)} 60%), linear-gradient(180deg, ${rgba(
          secondary.main,
          0.08
        )} 0%, ${light.main} 38%)`,
        backgroundAttachment: "fixed",
      },
      "a, a:link, a:visited": {
        textDecoration: "none",
      },
      "::selection": {
        backgroundColor: rgba(primary.main, 0.18),
      },
      // Custom, slim scrollbar for a more polished feel
      "*::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "*::-webkit-scrollbar-thumb": {
        backgroundColor: rgba(dark.main, 0.2),
        borderRadius: "8px",
      },
      "*::-webkit-scrollbar-thumb:hover": {
        backgroundColor: rgba(dark.main, 0.32),
      },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: borderRadius.xl,
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: 0,
        paddingTop: "0.55rem",
        paddingBottom: "0.55rem",
        paddingLeft: "1.15rem",
        paddingRight: "1.15rem",
        transition:
          "transform 200ms cubic-bezier(0.4,0,0.2,1), box-shadow 200ms ease, background-color 200ms ease",
        "&:active": {
          transform: "translateY(1px)",
        },
      },
      sizeLarge: {
        paddingTop: "0.75rem",
        paddingBottom: "0.75rem",
        fontSize: "0.95rem",
        borderRadius: borderRadius.xxl,
      },
      sizeSmall: {
        borderRadius: borderRadius.lg,
      },
      contained: {
        boxShadow: `0 2px 8px -2px ${rgba(dark.main, 0.25)}`,
        "&:hover": {
          boxShadow: `0 6px 16px -4px ${rgba(dark.main, 0.3)}`,
          transform: "translateY(-1px)",
        },
      },
      containedPrimary: {
        backgroundImage: linearGradient(
          colors.gradients.primary.main,
          colors.gradients.primary.state
        ),
        boxShadow: brandShadow,
        "&:hover": {
          boxShadow: brandShadowHover,
          transform: "translateY(-1px)",
        },
      },
      containedSecondary: {
        backgroundImage: linearGradient(
          colors.gradients.secondary.main,
          colors.gradients.secondary.state
        ),
        color: primary.main,
      },
      outlined: {
        borderWidth: borderWidth[2],
        "&:hover": {
          borderWidth: borderWidth[2],
          backgroundColor: rgba(primary.main, 0.06),
        },
      },
      text: {
        "&:hover": {
          backgroundColor: rgba(primary.main, 0.06),
        },
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.lg,
        transition: "background-color 200ms ease, color 200ms ease",
      },
    },
  },

  MuiCardHeader: {
    styleOverrides: {
      root: {
        paddingTop: "1rem",
        paddingBottom: "0.75rem",
      },
      title: {
        fontWeight: 700,
        letterSpacing: "-0.01em",
      },
      subheader: {
        color: text.main,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.xl,
        fontWeight: 600,
        letterSpacing: 0,
      },
      filled: {
        backgroundColor: rgba(primary.main, 0.1),
        color: primary.main,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.lg,
        fontWeight: 500,
        alignItems: "center",
      },
      standardError: {
        backgroundColor: rgba(error.main, 0.1),
      },
      standardInfo: {
        backgroundColor: rgba(info.main, 0.1),
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: borderRadius.xxl,
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: borderRadius.lg,
        marginTop: "0.5rem",
        border: `${borderWidth[1]} solid ${rgba(dark.main, 0.06)}`,
        boxShadow: softShadow,
      },
      list: {
        paddingTop: "0.4rem",
        paddingBottom: "0.4rem",
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.md,
        marginLeft: "0.4rem",
        marginRight: "0.4rem",
        fontWeight: 500,
        "&:hover": {
          backgroundColor: rgba(primary.main, 0.08),
        },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: borderRadius.xxl,
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.lg,
        backgroundColor: white.main,
        transition: "box-shadow 200ms ease, border-color 200ms ease",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: rgba(dark.main, 0.14),
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: rgba(primary.main, 0.5),
        },
        "&.Mui-focused": {
          boxShadow: `0 0 0 4px ${rgba(primary.main, 0.12)}`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: primary.main,
          borderWidth: borderWidth[1],
        },
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.xl,
        backgroundColor: rgba(dark.main, 0.07),
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: borderRadius.md,
        backgroundColor: rgba(dark.main, 0.92),
        fontSize: "0.75rem",
        fontWeight: 500,
        padding: "0.4rem 0.65rem",
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: "3px",
        borderRadius: "3px",
        backgroundColor: primary.main,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: 0,
        minHeight: "auto",
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.xl,
        backgroundColor: rgba(primary.main, 0.12),
      },
      bar: {
        borderRadius: borderRadius.xl,
      },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: 600,
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: rgba(dark.main, 0.08),
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: borderRadius.lg,
        "&:hover": {
          backgroundColor: rgba(primary.main, 0.06),
        },
      },
    },
  },
};

export default globalComponents;
